import { addDevTool } from 'debug/ostools'
import { shouldSuppressE2eOverlays } from 'e2e/shouldSuppressE2eOverlays'
import { useSyncExternalStore } from 'react'

/** In-memory only (no sessionStorage): survives SPA navigation, cleared on full reload. */
let tourForceHide = false
const listeners = new Set<() => void>()
let ostoolsRegistered = false

function emit(): void {
  for (const l of listeners) {
    l()
  }
}

/** Subscribe to tour force-hide changes; used by `useTourForceHide`. */
export function subscribeTourForceHide(callback: () => void): () => void {
  listeners.add(callback)
  return () => {
    listeners.delete(callback)
  }
}

export function getTourForceHideSnapshot(): boolean {
  return tourForceHide || shouldSuppressE2eOverlays()
}

export function setTourForceHide(suppressed: boolean): void {
  if (tourForceHide === suppressed) return
  tourForceHide = suppressed
  emit()
}

/**
 * Whether tour chrome (banners, walkthrough overlays) is superficially hidden for automation.
 * Reversible via `setTourForceHide(false)` / `ostools.tours.clearForceHide()`.
 */
export function useTourForceHide(): boolean {
  return useSyncExternalStore(subscribeTourForceHide, getTourForceHideSnapshot, () => false)
}

/** Register `ostools.tours.forceHide` / `clearForceHide` (once per app load). */
export function registerTourOstools(): void {
  if (typeof window === 'undefined' || ostoolsRegistered) return
  ostoolsRegistered = true

  addDevTool(
    'tours.forceHide',
    () => {
      setTourForceHide(true)
    },
    {
      help:
        'Temporarily hide all tour UI (banners, spotlights). In-memory only; cleared with tours.clearForceHide() or full reload. For Playwright / automation.',
    }
  )
  addDevTool(
    'tours.clearForceHide',
    () => {
      setTourForceHide(false)
    },
    { help: 'Show tours again after tours.forceHide().' }
  )
}

// Register as soon as this module loads (before PersistentContent effects / first paint).
// E2E calls `ostools.tours.forceHide()` immediately after login; a useEffect-only register races that.
if (typeof window !== 'undefined') {
  registerTourOstools()
}
